import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideEchartsCore } from 'ngx-echarts';
import { MessageService } from 'primeng/api';

import { BarChart, BoxplotChart, LineChart, PieChart, ScatterChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { blockMatrixApiInterceptor } from './core/interceptors/block-matrix-api.interceptor';
import { MockDataProviders } from './core/providers/mock-data.providers';
import { AddComparisonAsIncidentService } from './core/services/add-comparison-as-incident.service';
import { LanguageService } from './core/services/language.service';
import { OrganizationService } from './core/services/organization.service';
import { ADD_COMPARISON_AS_INCIDENT_CLIENT } from './core/tokens/add-comparison-as-incident.token';
import { USE_MOCKED_DATA } from './core/tokens/use-mocked-data.token';

// Initialize language service on app startup
export function initializeLanguage(languageService: LanguageService) {
  return () => languageService.init();
}

/** Loads current organization when a stored token exists (page refresh). */
export function initializeOrganization(organizationService: OrganizationService) {
  return () => organizationService.initialize();
}

echarts.use([
  LineChart,
  BarChart,
  PieChart,
  BoxplotChart,
  ScatterChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  DatasetComponent,
  TransformComponent,
  CanvasRenderer,
]);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideEchartsCore({ echarts }),
    provideHttpClient(withInterceptors([authInterceptor, blockMatrixApiInterceptor])),
    importProvidersFrom(TranslateModule.forRoot()),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json'
    }),
    {
      provide: USE_MOCKED_DATA,
      useValue: environment.useMockedData,
    },
    MockDataProviders.provideDashboardData(),
    MockDataProviders.provideApiStateClient(),
    MockDataProviders.provideFavoritesClient(),
    MockDataProviders.provideEmployeeVarianceManagerClient(),
    MockDataProviders.provideFeatureComparisonClient(),
    MockDataProviders.provideTimelineEventsClient(),
    MockDataProviders.provideEmailPreviewClient(),
    MockDataProviders.provideEmailAnalyticsClient(),
    MockDataProviders.provideEmailSendClient(),
    MockDataProviders.provideEmailResponseClient(),
    MockDataProviders.provideEmailResolutionResponsesClient(),
    MockDataProviders.provideCaseInvestigationTrackerClient(),
    MockDataProviders.provideIncidentTransactionsClient(),
    MockDataProviders.provideVarianceIncidentDetailsClient(),
    MockDataProviders.provideIncidentStatusClient(),
    MockDataProviders.provideFiltersClient(),
    MockDataProviders.provideCaseInvestigationClient(),
    { provide: ADD_COMPARISON_AS_INCIDENT_CLIENT, useClass: AddComparisonAsIncidentService },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      deps: [LanguageService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeOrganization,
      deps: [OrganizationService],
      multi: true,
    },
    MessageService,
  ],
};
